import { useMemo, useState } from "react";
import { Building2, Check, FileText, Loader2, X } from "lucide-react";
import type { UniverseEntity } from "@/types/brandUniverse.type";
import type { StageFact } from "../data/universeLayout";
import { useUniverseCompetitors } from "../api/useUniverseCompetitors";
import { KitModal } from "./kit/Modal";
import { KitButton } from "./kit/Button";
import { KitIconButton } from "./kit/IconButton";
import { KitInput } from "./kit/Input";
import { KitSelect, KitSelectItem } from "./kit/Select";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";

type Props = {
  brandId: string;
  entities: UniverseEntity[];
  facts: StageFact[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dev fixture harness: every action is a no-op that says so. */
  preview?: boolean;
};

/** Row actions are words, not bare icons: 32px tall, quiet until pointed at. */
/* Universe / Rival row: the row's actions are text capsules, muted until hovered. */
const ROW_ACTION =
  "co-row-action inline-flex h-8 items-center rounded-pill px-2.5 text-[12.5px] font-semibold text-co-muted transition-colors hover:bg-co-hover hover:text-co-text focus-visible:text-co-text";

/* Section labels (COMPETITOR ROSTER, GENERAL KNOWLEDGE): mono, tracked, text2. */
const SECTION_LABEL = "flex items-baseline gap-2 font-mono text-[10.5px] font-normal leading-[14px] uppercase tracking-[1.2px] text-co-text2";

/* Select (Universe / Rival row · Action, General fact row): the kit Select as
   a 32 capsule, the value Medium. */
const PILL_SELECT = "h-8 min-w-0 text-[13px]";

export default function CompetitorManagerModal({
  brandId,
  entities,
  facts,
  isOpen,
  onOpenChange,
  preview = false,
}: Props) {
  const competitors = useMemo(
    () => entities.filter((entity) => entity.kind === "competitor"),
    [entities],
  );
  const generalFacts = useMemo(
    () => facts.filter((fact) => fact.subjectKey === "competitors" && !fact.entityId),
    [facts],
  );
  const actions = useUniverseCompetitors(brandId);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fail = (cause: unknown, fallback: string) => {
    setError(getAiRequestErrorMessage(cause, fallback));
  };
  /** In the fixture harness nothing is written: say so and settle the form. */
  const previewOnly = (settle: () => void) => {
    queue.add({ title: "Preview: nothing was changed", variant: "info" });
    settle();
  };

  const addCompetitor = async () => {
    const name = newName.trim();
    if (!name || actions.create.isPending) return;
    setError(null);
    if (preview) return previewOnly(() => setNewName(""));
    try {
      await actions.create.mutateAsync(name);
      setNewName("");
    } catch (cause) {
      fail(cause, "Could not add that competitor. Try again.");
    }
  };

  const saveRename = async (entityId: string) => {
    const name = renameDraft.trim();
    if (!name || actions.rename.isPending) return;
    setError(null);
    if (preview) {
      return previewOnly(() => {
        setRenamingId(null);
        setRenameDraft("");
      });
    }
    try {
      await actions.rename.mutateAsync({ entityId, name });
      setRenamingId(null);
      setRenameDraft("");
    } catch (cause) {
      fail(cause, "Could not rename that competitor. Try again.");
    }
  };

  const mergeCompetitor = async () => {
    if (!mergeSourceId || !mergeTargetId || actions.merge.isPending) return;
    setError(null);
    if (preview) {
      return previewOnly(() => {
        setMergeSourceId(null);
        setMergeTargetId("");
      });
    }
    try {
      await actions.merge.mutateAsync({
        sourceEntityId: mergeSourceId,
        targetEntityId: mergeTargetId,
      });
      setMergeSourceId(null);
      setMergeTargetId("");
    } catch (cause) {
      fail(cause, "Could not merge those competitors. Try again.");
    }
  };

  const deleteCompetitor = async (entityId: string) => {
    if (actions.remove.isPending) return;
    setError(null);
    if (preview) return previewOnly(() => setDeletingId(null));
    try {
      await actions.remove.mutateAsync(entityId);
      setDeletingId(null);
    } catch (cause) {
      fail(cause, "Could not delete that competitor. Try again.");
    }
  };

  const fileFact = async (factId: string, entityId: string) => {
    if (!entityId || actions.fileFact.isPending) return;
    setError(null);
    if (preview) return previewOnly(() => undefined);
    try {
      await actions.fileFact.mutateAsync({ factId, entityId });
    } catch (cause) {
      fail(cause, "Could not file that fact. Try again.");
    }
  };

  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Manage competitors"
      modalClassName="co-competitor-modal flex flex-col !overflow-hidden !w-[min(calc(100vw-2rem),720px)] !max-w-[720px]"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto pe-1">
        <section aria-labelledby="competitor-roster-title" className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            <h3 id="competitor-roster-title" className={SECTION_LABEL}>
              Competitor roster
              <span className="text-co-faint">{competitors.length}</span>
            </h3>
            <p className="text-[13.5px] leading-[1.5] text-co-muted">
              Each rival gets its own facts, campaigns and knowledge.
            </p>
          </div>

          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void addCompetitor();
            }}
          >
            <KitInput
              aria-label="Competitor name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Add a competitor"
              wrapperClassName="min-w-0 flex-1"
            />
            <KitButton type="submit" className="h-11 shrink-0 px-3.5 text-[13px]" loading={actions.create.isPending}>
              Add
            </KitButton>
          </form>

          {competitors.length === 0 ? (
            <div className="rounded-card border border-dashed border-co-line px-4 py-5 text-center">
              <Building2 className="mx-auto size-4 text-co-faint" aria-hidden />
              <p className="mt-2 text-[12px] text-co-muted">No named competitors yet.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {competitors.map((entity) => {
                const factCount = facts.filter((fact) => fact.entityId === entity.id).length;
                const renaming = renamingId === entity.id;
                const merging = mergeSourceId === entity.id;
                const confirmingDelete = deletingId === entity.id;
                return (
                  <li key={entity.id} className="py-[18px] ps-3.5">
                    <div className="flex min-w-0 items-center gap-4">
                      <Building2 className="size-3.5 shrink-0 text-co-muted" aria-hidden />
                      {renaming ? (
                        <form
                          className="flex min-w-0 flex-1 gap-1"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void saveRename(entity.id);
                          }}
                        >
                          <KitInput
                            aria-label={`Rename ${entity.name}`}
                            value={renameDraft}
                            onChange={(event) => setRenameDraft(event.target.value)}
                            autoFocus
                            wrapperClassName="min-w-0 flex-1"
                          />
                          <KitIconButton
                            type="submit"
                            icon={actions.rename.isPending ? Loader2 : Check}
                            aria-label={`Save ${entity.name}`}
                            className="self-center"
                            iconClassName={actions.rename.isPending ? "animate-spin" : undefined}
                          />
                          <KitIconButton icon={X} onClick={() => setRenamingId(null)} aria-label="Cancel rename" className="self-center" />
                        </form>
                      ) : (
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-medium leading-[18px] text-co-text">{entity.name}</p>
                          <p className="mt-1.5 text-[11px] leading-[15px] text-co-faint">
                            {factCount} fact{factCount === 1 ? "" : "s"}
                          </p>
                        </div>
                      )}

                      {/* Rival row · Action: merging swaps the row's actions for
                          MERGE INTO, the rival picker and Merge. */}
                      {merging ? (
                        <div className="flex shrink-0 items-center gap-2.5">
                          <span aria-hidden className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-co-text2">
                            Merge into
                          </span>
                          <KitSelect
                            aria-label={`Merge ${entity.name} into`}
                            placeholder="Choose competitor"
                            selectedKey={mergeTargetId || null}
                            onSelectionChange={(key) => setMergeTargetId(key ? String(key) : "")}
                            buttonClassName={`${PILL_SELECT} w-[190px]`}
                          >
                            {competitors.filter((option) => option.id !== entity.id).map((option) => (
                              <KitSelectItem key={option.id} id={option.id} textValue={option.name}>{option.name}</KitSelectItem>
                            ))}
                          </KitSelect>
                          <KitButton type="button" variant="secondary" isDisabled={!mergeTargetId} loading={actions.merge.isPending} onClick={() => void mergeCompetitor()}>
                            Merge
                          </KitButton>
                        </div>
                      ) : null}

                      {!renaming && !merging ? (
                        <div className="flex shrink-0 items-center gap-0.5">
                          {confirmingDelete ? (
                            <>
                              <span className="me-2 text-[12.5px] font-semibold text-co-text">Delete rival?</span>
                              <KitButton type="button" variant="ghost" onClick={() => setDeletingId(null)}>
                                Cancel
                              </KitButton>
                              <KitButton type="button" variant="danger" className="ms-2" loading={actions.remove.isPending} onClick={() => void deleteCompetitor(entity.id)}>
                                Delete
                              </KitButton>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setRenamingId(entity.id);
                                  setRenameDraft(entity.name);
                                }}
                                className={ROW_ACTION}
                                aria-label={`Rename ${entity.name}`}
                              >
                                Rename
                              </button>
                              {competitors.length > 1 ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMergeSourceId(merging ? null : entity.id);
                                    setMergeTargetId("");
                                  }}
                                  className={ROW_ACTION}
                                  aria-label={`Merge ${entity.name} into another competitor`}
                                  aria-expanded={merging}
                                >
                                  Merge into…
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => setDeletingId(entity.id)}
                                className={`${ROW_ACTION} hover:text-co-danger focus-visible:text-co-danger`}
                                aria-label={`Delete ${entity.name}`}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>

                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section aria-labelledby="general-knowledge-title" className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            <h3 id="general-knowledge-title" className={SECTION_LABEL}>
              General knowledge
            </h3>
            <p className="text-[13.5px] leading-[1.5] text-co-muted">
              General is valid. File a fact only when it clearly belongs to one rival.
            </p>
          </div>
          {generalFacts.length === 0 ? (
            <p className="text-[13px] text-co-faint">
              Nothing waiting to be filed.
            </p>
          ) : competitors.length === 0 ? (
            <p className="text-[13px] text-co-faint">
              Add a competitor before filing general facts.
            </p>
          ) : (
            <ul className="overflow-hidden rounded-card bg-co-surface">
              {generalFacts.map((fact) => (
                <li key={fact.id} className="flex flex-wrap items-center gap-4 border-t border-co-line py-3 ps-3.5 first:border-t-0">
                  <FileText className="size-3.5 shrink-0 text-co-muted" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium leading-[18px] text-co-text">{fact.label}</p>
                    {fact.desc ? <p className="mt-1 truncate text-[11.5px] leading-4 text-co-faint">{fact.desc}</p> : null}
                  </div>
                  {/* No kept value: picking a rival files the fact, which leaves this list. */}
                  <KitSelect
                    aria-label={`File ${fact.label}`}
                    placeholder="File under…"
                    selectedKey={null}
                    isDisabled={actions.fileFact.isPending}
                    onSelectionChange={(key) => {
                      if (key) void fileFact(fact.id, String(key));
                    }}
                    buttonClassName={`${PILL_SELECT} w-[170px]`}
                  >
                    {competitors.map((entity) => (
                      <KitSelectItem key={entity.id} id={entity.id} textValue={entity.name}>{entity.name}</KitSelectItem>
                    ))}
                  </KitSelect>
                </li>
              ))}
            </ul>
          )}
        </section>

        {error ? <p role="alert" className="pt-1 text-[12.5px] text-co-alert-ink">{error}</p> : null}
      </div>
    </KitModal>
  );
}
